import React, { useEffect, useState } from 'react';
import styles from '../../styles/components/sections/ServicesSection.module.css';
import { replaceImageUrl } from '../../utils/helpers/imageUtils';
import { sanitizeHtml } from '../../utils/helpers/sanitize';
import { useDarkMode } from '../../utils/hooks/useDarkMode';
import { OptimizedImage } from '../common';

/**
 * Services Section Component
 * Displays a centered section title/description with service cards
 *
 * @param {Object} props
 * @param {Object} props.data - The services data
 * @param {String} props.data.title - Main section title
 * @param {String} props.data.description - Main section description
 * @param {Array} props.data.Services - Array of service objects
 * @param {String} props.className - Optional additional CSS class
 */

const ServicesSection = ({ data, className = '' }) => {
    // const { isDarkMode } = useDarkMode();
    const [isDarkMode, setIsDarkMode] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [isClient, setIsClient] = useState(false);



    // Set client-side flag
    useEffect(() => {
        setIsClient(true);
    }, []);

    // Detect system dark mode preference
    useEffect(() => {
        if (!isClient) return;

        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        setIsDarkMode(mediaQuery.matches);

        const handleChange = e => {
            setIsDarkMode(e.matches);
            updateTheme(e.matches ? 'dark' : 'light');
        };

        mediaQuery.addEventListener('change', handleChange);

        // Check for saved theme preference
        const savedTheme = localStorage.getItem('theme');
        if (savedTheme) {
            setIsDarkMode(savedTheme === 'dark');
            updateTheme(savedTheme);
        } else {
            // Set initial theme based on system preference
            updateTheme(mediaQuery.matches ? 'dark' : 'light');
        }

        return () => mediaQuery.removeEventListener('change', handleChange);
    }, [isClient]);

    // Detect mobile device
    useEffect(() => {
        if (!isClient) return;

        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);

        return () => window.removeEventListener('resize', checkMobile);
    }, [isClient]);

    // Update theme on document
    const updateTheme = theme => {
        document.documentElement.setAttribute('data-theme', theme);
        localStorage.setItem('theme', theme);
    };
    if (!data) return null;

    // Helper function to create HTML from the rich text content
    const createMarkup = htmlContent => {
        const sanitizedContent = sanitizeHtml(htmlContent);
        return { __html: sanitizedContent };
    };

    const services = data.Services || [];

    return (
        <section className={`py-6 ${className}`}>
            <div className="tw-container">
                {/* Section Header */}
                <div className="d-flex flex-column justify-content-center ">
                    <h2 className={`services-heading ${isDarkMode ? styles['services-heading-dark'] : ''}`}>{data.title}</h2>
                    {data.description && <div className={`services-description ${isDarkMode ? styles['services-description-dark'] : ''}`}>{data.description}</div>}
                    <div className="row"></div>
                </div>
                {/* Services Grid */}
                <div className="row g-3">
                    {services.map((service, index) => (
                        <div key={service.id || index} className={`col-md-4 py-3 `}>
                            <div className={`${styles['service-card']} ${isDarkMode ? styles['service-dark-card'] : ''}`}>
                                {service.image?.url && (
                                    <OptimizedImage
                                        src={replaceImageUrl(service.image.url)}
                                        alt={service.name || 'Service image'}
                                        width={284}
                                        height={195}
                                        priority={false}
                                        loading="lazy"
                                        sizes="(max-width: 768px) 100vw, 33vw"
                                        quality={78}
                                        className={`${styles['card-img-top']} ${isDarkMode ? styles['card-img-dark'] : ''}`}
                                        style={{ width: '100%' }}
                                    />
                                )}
                                <div className="card-body">
                                    <h3 className={`${styles['card-title']} ${isDarkMode ? styles['card-title-dark'] : ''}`}> {service.name} </h3>
                                    {service.description && (
                                        <div className={`${styles['card-text']} ${isDarkMode ? styles['card-text-dark'] : ''} text-muted mb-3`} dangerouslySetInnerHTML={createMarkup(service.description)} />
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default ServicesSection;
